import imageLiveStream from 'APP_ROOT/components/pages/dashboard/EventMedia/images/live-stream-w64.svg';
import { strings } from 'APP_ROOT/localization';
import moment from 'moment';
// eslint-disable-next-line import/no-webpack-loader-syntax
import mapboxgl from '!mapbox-gl'; // https://docs.mapbox.com/mapbox-gl-js/guides/install/#excluding-mapbox-gl-js-explicitly-from-transpilation

/* Return a Date object from the input.  It is either: 
    - null - return itself
    - a Date object (has getTime defined) - return itself
    - a string without 'Z' on the end - add 'Z' and return new Date (Z ensures UTC)
    - a string with 'Z' on the end - return new Date (Z ensures UTC)
*/
function getDateObject(ts) {
    if(!ts) return ts;
    return ts.getTime ? ts : ts.endsWith('Z') ? new Date(ts) : new Date("" + ts + "Z");

}

// Pop up on mouse over for a route layer
// NOTE that the timestamps need to be in the properties of the layer as an array equal in length to the coordinates length.

const ZOOM_FOR_DETAILS_DURATION = 1000; // 1 second

export default class MapboxRoutePopup {

    constructor(videoAvailable, streamAtTimeCallback) {

        this.map = undefined;
        this.layerid = undefined;
        this.sourceid = undefined;
        this.source = undefined; // corresponds to sourceid, obviously

        this.popups = [];

        this.videoAvailable = videoAvailable;
        this.streamAtTimeCallback = streamAtTimeCallback;

        this.removePopupsTimeout = undefined;
        this.mouseEnterLineTimeout = undefined;
    }

    addToMap = (map, layerid, sourceid) => {

        map.on('mouseenter', layerid, this.mouseEnterLine);
        map.on('mouseleave', layerid, this.mouseExitLine);

        this.map = map;
        this.layerid = layerid;
        this.sourceid = sourceid;
        this.source = this.map.getSource(sourceid);
    }

    removeFromMap = (map, layerid) => {

        map.off('mouseenter', layerid, this.mouseEnterLine); // without map.off calls, significant memory leak popup duplication
        map.off('mouseleave', layerid, this.mouseExitLine);

        // Clearing timeouts as due diligence.  There is no evidence that this causes/resolves popup duplication.  But not clearing timeouts is a standard memory leak faux pas.
        clearTimeout(this.mouseEnterLineTimeout);
        //clearTimeout(this.removePopupsTimeout); cleared in removePopups
        this.removePopups();
    }

    onClickZoomForDetails = (event) => {
        const geostampJSON = event.currentTarget.dataset.geostampJSON; // using currentTarget to get the element/target that actually has the event listener and therefore data attribute

        if (!geostampJSON) {
            console.error("MapboxRoutePopup popupContentElement geostampJSON not found in currentTarget custom data attributes");
            return;
        }

        const geostamp = JSON.parse(geostampJSON);

        // zoom the map
        this.map.easeTo( {
            center: geostamp,
            zoom: 12,
            duration: ZOOM_FOR_DETAILS_DURATION
        });

        // update popup after map zoom
        this.mouseEnterLineTimeout = setTimeout( () => {

            this.mouseEnterLine( {
                    ...event,
                    // add 2 required MapMouseEvent properties
                    lngLat: { lng: geostamp[0], lat: geostamp[1] },
                    point: this.map.project(geostamp)
                });

        }, ZOOM_FOR_DETAILS_DURATION + 100 ); // adding a 10th of a second delay after zoom completes before updating the popup
    }

    onClickTimelineElement = (event) => {

        const streamTimestampString = event.currentTarget.dataset.timestamp; // using currentTarget to get the element/target that actually has the event listener and therefore data attribute

        if (!streamTimestampString) {
            console.error("MapboxRoutePopup popupContentElement geostampJSON not found in currentTarget custom data attributes");
            return;
        }
        this.streamAtTimeCallback(streamTimestampString);

    }

    mouseEnterLine = (mapMouseEvent) => { //https://docs.mapbox.com/mapbox-gl-js/api/events/#mapmouseevent

        this.removePopups();

        let currentZoom = this.map.getZoom();

        const popupContentElement = document.createElement("div");

        let nearestGeoStamp = null;
        let nearestGeoStampDistance = Infinity;

        let threshold = 1.0; // default for zoomed-out

        if (currentZoom > 8) {
            // Scale threshold based on zoom level 
            if (currentZoom >= 14) {
                threshold = 0.01;
            } else if (currentZoom >= 12) {
                threshold = 0.1;
            } else if (currentZoom >= 11) {
                threshold = 0.5;
            } /*else { (default)
                threshold = 1.0;
            }*/            
        }

        const lineCoordinates = this.source._data.features[0].geometry.coordinates;        

        const nearestPoints = this.pointsUnderCursor(lineCoordinates, mapMouseEvent.lngLat, threshold);

        nearestPoints.forEach( (pointDetails) => { // {point: [lng, lat], index:, angle:, t:, distance:}
            if (pointDetails.distance < nearestGeoStampDistance) {
                nearestGeoStamp = pointDetails.point;
                nearestGeoStampDistance = pointDetails.distance;
            }
        });

        if (currentZoom <= 8) { // If we are zoomed out too much, display a "Zoom for details" popup
            
            popupContentElement.dataset.popupType = "ZOOM_FOR_DETAILS";

            popupContentElement.innerHTML = "<div>" + strings.zoom_for_details + "</div>";

            popupContentElement.dataset.geostampJSON = JSON.stringify(nearestGeoStamp); // add required event detail as a custom data attribute

            popupContentElement.addEventListener('click', this.onClickZoomForDetails);

        } else {

            const sourceRouteTimestamps = this.source._data.features[0].properties.timestamps;
            const canViewVideoStream = this.source._data.features[0].properties.canViewVideoStream;

            if (!sourceRouteTimestamps) {
                console.error('MapboxRoutePopup mouseEnterLine no route timestamps in route properties');
                sourceRouteTimestamps = [];
            }

            nearestPoints.forEach( (pointDetails, i) => { // {point: [lng, lat], index:, angle:, t:, distance:}

                const index = pointDetails.index + (pointDetails.t > 0.5 ? 1 : 0);
                pointDetails.ts = sourceRouteTimestamps[index];

                if (!pointDetails.ts) {
                    console.error('MapboxRoutePopup mouseEnterLine no route timestamp in route properties for index', index);
                    return;
                }

                if (i > 0) {
                    let curTs = getDateObject(pointDetails.ts);
                    let prevTs = getDateObject(nearestPoints[i-1].ts);

                    // If 2 adjacent timestamps are within 1 minute (60k microseconds) of each other, skip the second one
                    if (Math.abs(curTs - prevTs) < 60000) {
                        return;
                    }
                }

                const timeElement = document.createElement("div");
                timeElement.style.display = "flex";
                timeElement.style.alignItems = "center"; // align arrow, timestamp, and stream button vertically center
                timeElement.style.position = "relative"; // make the z-index: 10 absolute positioned element 'relative' to this wrapper instead of a parent higher up the tree
                timeElement.dataset.timestamp = typeof pointDetails.ts === "string" ? pointDetails.ts : pointDetails.ts.toISOString();  // add required event detail as a custom data attribute

                let baseInnerHTML = "<div style='position: relative; width: 22.627px; height: 22.627px; padding: 0; margin: 0;'><div style='position: absolute; top: 3.376px; left: 3.376px; font-size: 16px; line-height: 16px; margin: 0; width: 16px; height: 16px; transform: rotate(" + -pointDetails.angle + "deg) translateY(-3px);'>&#8594;</div></div>" + "<div style='margin: 0;'>" + moment.utc(pointDetails.ts, moment.ISO_8601).local().format('h:mm:ss a') + "</div>";

                // If account has the feature
                // and user has the permission
                if (this.videoAvailable && canViewVideoStream) { // I had difficulting avoiding adding the check here (componentDidUpdate, for example, had unexpected challenges)

                    timeElement.className = "mapbox-popup-streaming-timeline-element";

                    popupContentElement.dataset.popupType = "STREAMING";

                    baseInnerHTML += `<div style="z-index: 10; position: absolute; top: -15px; left: -10%; padding: 0; margin: 0; width: 120%; height: 62px;"></div>&nbsp;&nbsp;<img src=${imageLiveStream} height=24 style="margin-left: auto; marting-right: 0; filter:invert(); vertical-align: -5px;" />`;

                    // Click anywhere on that time will stream
                    timeElement.addEventListener('click', this.onClickTimelineElement);

                }

                timeElement.innerHTML = baseInnerHTML;

                popupContentElement.appendChild(timeElement);
            });
        
        }

        const popupElement = document.createElement("div");
        popupElement.style.position = "relative"; // make sure absolute-positioned child div is position relative to this popup element
        popupElement.style.cursor = "pointer"; // make it look like a button without inheriting all the theme CSS for buttons
        popupElement.style.padding = "0px 5px";
        popupElement.style.fontSize = "16px";
        popupElement.style.lineHeight = "16px";

        popupElement.appendChild(popupContentElement);

        popupElement.onmouseleave = () => {

            this.map.on('mouseleave', this.layerid, this.mouseExitLine);
            this.mouseExitLine();
        }
        popupElement.onmouseenter = () => {
            // Came out and back in within 1 second, stay in
            this.map.off('mouseleave', this.layerid, this.mouseExitLine); 
            this.mouseUnExitLine();
        }
        this.map.off('mouseleave', this.layerid, this.mouseExitLine); // remove route line mouseleave (it will be performed by popupElement's mouseout and also re-added as a listener)

        this.popups.push(new mapboxgl.Popup({ offset: 1 })
            .setLngLat( nearestGeoStamp )
            .setDOMContent(popupElement)
            .addTo(this.map));
    }

    mouseExitLine = () => {
        this.removePopupsTimeout = setTimeout(() => {
            this.removePopups();
        }, 1000);
    }

    mouseUnExitLine = () => {
        clearTimeout(this.removePopupsTimeout);
    }

    removePopups = () => {

        clearTimeout(this.removePopupsTimeout);

        this.popups.forEach((mapboxPopup, index, object) => {

            for (var i = 0; i < mapboxPopup._content.children.length; i++) { // forEach n/a

                const mapboxPopupChild = mapboxPopup._content.children[i];
                if (mapboxPopupChild.lastChild.dataset) {
                    switch (mapboxPopupChild.lastChild.dataset.popupType) {
                        case "STREAMING":
                            const streamingTimelineElements = mapboxPopupChild.lastChild.children;
                            for (let i=0; i < streamingTimelineElements.length; i++) {
                                if (streamingTimelineElements[i].className !== "mapbox-popup-streaming-timeline-element") {
                                    console.warn("MapboxRoutePopup removePopups for STREAMING popup type has unexpeccted children, timeline element not found.");
                                    continue;
                                }
                                streamingTimelineElements[i].removeEventListener('click', this.onClickTimelineElement);
                            }
                            break;
                        case "ZOOM_FOR_DETAILS":
                            mapboxPopupChild.removeEventListener('click', this.onClickZoomForDetails);
                            break;
                        default:
                            break;
                    }
                }
            }
             mapboxPopup.remove();
        });

        this.popups = [];

    }

    /* Algorithm adapted from cheapRuler

        ISC License

        Copyright (c) 2020, Mapbox

        Permission to use, copy, modify, and/or distribute this software for any purpose
        with or without fee is hereby granted, provided that the above copyright notice
        and this permission notice appear in all copies.

        THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
        REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY AND
        FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
        INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM LOSS
        OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR OTHER
        TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR PERFORMANCE OF
        THIS SOFTWARE.
    */
    pointsUnderCursor = (line, pnt, threshold) => {
        // Initialize LL to km conversion

        // Curvature formulas from https://en.wikipedia.org/wiki/Earth_radius#Meridional

        // Values that define WGS84 ellipsoid model of the Earth
        const RE = 6378.137; // equatorial radius
        const FE = 1 / 298.257223563; // flattening

        const E2 = FE * (2 - FE);
        const RAD = Math.PI / 180;

        const m = RAD * RE;
        const coslat = Math.cos(pnt.lat * RAD);
        const w2 = 1 / (1 - E2 * (1 - coslat * coslat));
        const w = Math.sqrt(w2);

        // multipliers for converting longitude and latitude degrees into distance
        const kx = m * w * coslat;        // based on normal radius of curvature
        const ky = m * w * w2 * (1 - E2); // based on meridonal radius of curvature

        const sqThreshold = threshold * threshold;

        // Iterate through the line.  As we approach

        let p = Array();
        p[0] = pnt.lng;
        p[1] = pnt.lat;

        let results = Array();

        let minDist = Infinity;
        let minX, minY, minI, minT;

        for (let i = 0; i < line.length - 1; i++) {

            let x = line[i][0];
            let y = line[i][1];
            let dx = this.wrap(line[i + 1][0] - x) * kx;  // x portion of line vector
            let dy = (line[i + 1][1] - y) * ky;  // y portion of line vector
            let t = 0;

            if (dx !== 0 || dy !== 0) {  // line segment non-zero in length
                t = (this.wrap(p[0] - x) * kx * dx + (p[1] - y) * ky * dy) / (dx * dx + dy * dy);

                if (t > 1) {
                    x = line[i + 1][0];
                    y = line[i + 1][1];

                } else if (t > 0) {
                    x += (dx / kx) * t;
                    y += (dy / ky) * t;
                }
            }

            dx = this.wrap(p[0] - x) * kx;
            dy = (p[1] - y) * ky;

            const sqDist = dx * dx + dy * dy;

            if (sqDist < minDist) {

                minDist = sqDist;
                minX = x;
                minY = y;
                minI = i;
                minT = t;
            }
            else if (sqDist > (minDist + sqThreshold)) {

                /* We found a local min distance */
                /* Store the current minDist and reset everything to start again */

                if(minDist < sqThreshold)
                {
                    let segx = line[minI+1][0] - line[minI][0];
                    let segy = line[minI+1][1] - line[minI][1];

                    results.push({
                            point: [minX, minY],
                            index: minI,
                            angle: Math.atan2(segy, segx)*180/Math.PI,
                            t: Math.max(0, Math.min(1, minT)),
                            distance: minDist
                        });

                    minDist = Infinity;
                    minX = null;
                    minY = null;
                    minI = null;
                    minT = null;
                }
            }
        }


        // Add the final point, if necessary
        if(results.length === 0 || minDist < sqThreshold)
        {
            let segx = line[minI+1][0] - line[minI][0];
            let segy = line[minI+1][1] - line[minI][1];

            results.push({
                    point: [minX, minY],
                    index: minI,
                    angle: Math.atan2(segy, segx)*180/Math.PI,
                    t: Math.max(0, Math.min(1, minT)),
                    distance: minDist
                });

            minDist = Infinity;
            minX = null;
            minY = null;
            minI = null;
            minT = null;
        }

        return results;

        
    }

    wrap(deg) {
        while (deg < -180) deg += 360;
        while (deg > 180) deg -= 360;
        return deg;
    }


}
